import { useCallback } from 'react';
import { AxiosRequestConfig } from 'axios';
import {
  CreateGroupRequest,
  CreateGroupResponse,
  GetGroupsV2Response,
  GetGroupByIdV2Response,
  NoContentResult,
  DeleteBulkGroupRequest,
  DeleteBulkGroupResponse,
  GetGroupsByVaultIdResponse,
  GetLowSecurityGroupsResponse,
  Operation,
} from '@uniqkey-backend-organization-web/api-client';
import { useApiClients } from '../../contexts/APIClientsContext';
import { TGetGroupsMethod, TGetGroupsByVaultIdMethod } from './interfaces';
import { dataExtractor } from '../../helpers/apiClients';

const useEmployeeGroupsAPI = () => {
  const { employeeGroupsAPIClient } = useApiClients();

  const createGroup = useCallback(
    (
      params: CreateGroupRequest,
      options?: AxiosRequestConfig,
    ): Promise<CreateGroupResponse> => employeeGroupsAPIClient
      .apiV1GroupsPost(params, options)
      .then(dataExtractor<CreateGroupResponse>()),
    [employeeGroupsAPIClient],
  );

  const getGroups = useCallback<TGetGroupsMethod>(
    (params, axiosRequestConfig) => {
      const {
        page,
        pageLength,
        canManageVaults = undefined,
        employeeAccountId = undefined,
        searchQuery = undefined,
        orderPropertyName = undefined,
        isDescending = undefined,
        isSecurityScoreEmpty = undefined,
        maxSecurityScore = undefined,
        minSecurityScore = undefined,
      } = params;
      return employeeGroupsAPIClient.apiV2GroupsGet(
        page,
        pageLength,
        canManageVaults,
        employeeAccountId,
        searchQuery,
        orderPropertyName,
        isDescending,
        isSecurityScoreEmpty,
        maxSecurityScore,
        minSecurityScore,
        axiosRequestConfig,
      ).then(dataExtractor<GetGroupsV2Response>());
    },
    [employeeGroupsAPIClient],
  );

  const getGroupsByVaultId = useCallback<TGetGroupsByVaultIdMethod>(
    (params, axiosRequestConfig) => {
      const {
        page,
        pageLength,
        vaultId,
        searchQuery = undefined,
        orderPropertyName = undefined,
        isDescending = undefined,
      } = params;
      return employeeGroupsAPIClient.apiV1GroupsGetByVaultIdGet(
        page,
        pageLength,
        vaultId,
        searchQuery,
        orderPropertyName,
        isDescending,
        axiosRequestConfig,
      ).then(dataExtractor<GetGroupsByVaultIdResponse>());
    },
    [employeeGroupsAPIClient],
  );

  const getGroupById = useCallback(
    (
      groupId: string,
      options?: AxiosRequestConfig,
    ): Promise<GetGroupByIdV2Response> => employeeGroupsAPIClient
      .apiV2GroupsGroupIdGet(groupId, options)
      .then(dataExtractor<GetGroupByIdV2Response>()),
    [employeeGroupsAPIClient],
  );

  const patchGroupById = useCallback(
    (groupId: string, operations: Array<Operation>, options?: AxiosRequestConfig)
      : Promise<NoContentResult> => employeeGroupsAPIClient
      .apiV1GroupsGroupIdPatch(groupId, operations, options)
      .then(dataExtractor<NoContentResult>()),
    [employeeGroupsAPIClient],
  );

  const deleteGroups = useCallback(
    (
      deleteBulkGroupRequest: DeleteBulkGroupRequest,
      options?: AxiosRequestConfig,
    ): Promise<DeleteBulkGroupResponse> => employeeGroupsAPIClient
      .apiV1GroupsDeleteBulkPost(deleteBulkGroupRequest, options)
      .then(dataExtractor<DeleteBulkGroupResponse>()),
    [employeeGroupsAPIClient],
  );

  const getLowSecurityScoreGroups = useCallback(
    (options?: AxiosRequestConfig): Promise<GetLowSecurityGroupsResponse> => employeeGroupsAPIClient
      .apiV2GroupsGetLowSecurityScoreGroupsGet(options)
      .then(dataExtractor<GetLowSecurityGroupsResponse>()),
    [employeeGroupsAPIClient],
  );

  return {
    createGroup,
    getGroups,
    getGroupsByVaultId,
    getGroupById,
    patchGroupById,
    deleteGroups,
    getLowSecurityScoreGroups,
  };
};

export default useEmployeeGroupsAPI;
